// src/utils/connectionTest.js
// Connection testing utility for production deployment

import axios from 'axios';
import API_CONFIG from '../config/api';

export class ConnectionTester {
  constructor() {
    this.baseURL = API_CONFIG.BASE_URL;
    this.testResults = {
      api: false,
      cors: false,
      auth: false,
      endpoints: {}
    };
  }

  // Test basic API connectivity
  async testAPIConnection() {
    try {
      const response = await axios.get(`${this.baseURL}/api/health/`, {
        timeout: 5000,
        headers: {
          'Content-Type': 'application/json',
        }
      });
      
      this.testResults.api = response.status === 200;
      return {
        success: true,
        status: response.status,
        data: response.data
      };
    } catch (error) {
      this.testResults.api = false;
      return {
        success: false,
        error: error.message,
        status: error.response?.status,
        details: this.getErrorDetails(error)
      };
    }
  }

  // Test CORS configuration
  async testCORS() {
    try {
      // Use fetch instead of axios for CORS testing
      const response = await fetch(`${this.baseURL}/api/token/`, {
        method: 'OPTIONS',
        headers: {
          'Origin': window.location.origin,
          'Access-Control-Request-Method': 'POST',
          'Access-Control-Request-Headers': 'Content-Type, Authorization',
        }
      });
      
      this.testResults.cors = response.status === 200 || response.status === 204;
      return {
        success: this.testResults.cors,
        headers: Object.fromEntries(response.headers.entries()),
        status: response.status
      };
    } catch (error) {
      this.testResults.cors = false;
      return {
        success: false,
        error: error.message,
        details: this.getErrorDetails(error)
      };
    }
  }

  // Test authentication endpoints
  async testAuthEndpoints() {
    const endpoints = [
      { 
        name: 'login', 
        url: '/api/token/', 
        method: 'POST',
        data: { username: 'test', password: 'test' }
      },
      { 
        name: 'refresh', 
        url: '/api/token/refresh/', 
        method: 'POST',
        data: { refresh: 'test_token' }
      },
      { 
        name: 'user_info', 
        url: '/chatbot/me/', 
        method: 'GET',
        data: null
      }
    ];

    const results = {};

    for (const endpoint of endpoints) {
      try {
        const response = await axios({
          method: endpoint.method,
          url: `${this.baseURL}${endpoint.url}`,
          timeout: 5000,
          headers: {
            'Content-Type': 'application/json',
          },
          data: endpoint.data || undefined
        });
        
        results[endpoint.name] = {
          success: true,
          status: response.status
        };
      } catch (error) {
        // For auth endpoints, we expect certain error codes as "successful" responses
        const expectedErrors = {
          'login': [400, 401], // 400 = bad request (missing credentials), 401 = invalid credentials
          'refresh': [400, 401], // 400 = bad request, 401 = invalid token
          'user_info': [401] // 401 = unauthorized (no token)
        };
        
        const isExpectedError = expectedErrors[endpoint.name]?.includes(error.response?.status);
        
        results[endpoint.name] = {
          success: isExpectedError, // Consider expected errors as "successful" endpoint availability
          status: error.response?.status,
          error: error.message,
          expected: isExpectedError ? 'Endpoint available (expected error)' : 'Unexpected error'
        };
      }
    }

    this.testResults.endpoints = results;
    this.testResults.auth = Object.values(results).some(r => r.success);
    
    return results;
  }

  // Comprehensive connection test
  async runFullTest() {
    console.log('🔍 Starting connection tests...');
    
    const results = {
      timestamp: new Date().toISOString(),
      baseURL: this.baseURL,
      tests: {}
    };

    // Test API connection
    console.log('📡 Testing API connection...');
    results.tests.api = await this.testAPIConnection();

    // Test CORS
    console.log('🌐 Testing CORS configuration...');
    results.tests.cors = await this.testCORS();

    // Test auth endpoints
    console.log('🔐 Testing authentication endpoints...');
    results.tests.auth = await this.testAuthEndpoints();

    // Generate summary
    results.summary = this.generateSummary();
    
    console.log('✅ Connection tests completed:', results.summary);
    
    return results;
  }

  // Generate test summary
  generateSummary() {
    const totalTests = 3; // api, cors, auth
    const passedTests = [
      this.testResults.api,
      this.testResults.cors,
      this.testResults.auth
    ].filter(Boolean).length;

    return {
      total: totalTests,
      passed: passedTests,
      failed: totalTests - passedTests,
      successRate: Math.round((passedTests / totalTests) * 100),
      status: passedTests === totalTests ? 'PASSED' : 'FAILED'
    };
  }

  // Get detailed error information
  getErrorDetails(error) {
    if (error.response) {
      return {
        status: error.response.status,
        statusText: error.response.statusText,
        data: error.response.data,
        headers: error.response.headers
      };
    } else if (error.request) {
      return {
        type: 'Network Error',
        message: 'No response received from server'
      };
    } else {
      return {
        type: 'Request Error',
        message: error.message
      };
    }
  }

  // Get connection status
  getConnectionStatus() {
    return {
      isConnected: this.testResults.api,
      hasCORS: this.testResults.cors,
      hasAuth: this.testResults.auth,
      overall: this.testResults.api && this.testResults.cors
    };
  }
}

// Export singleton instance
export const connectionTester = new ConnectionTester();

// Export convenience functions
export const testConnection = () => connectionTester.runFullTest();
export const getConnectionStatus = () => connectionTester.getConnectionStatus();
